import { useState, useEffect } from 'react';

import api from 'api';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

const useCriticalElements = (clientId, year) => {
  const [criticalElements, setCriticalElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentUser = useCurrentUser();

  const fetchCriticalElementsWithGoals = async () => {
    if (!clientId || !currentUser?.data?.oktaUserId) return;

    try {
      setLoading(true);

      const response = await api.getAllCriticalElements({
        userId: currentUser.data.oktaUserId,
        clientId,
        year,
      });

      if (!response.data || !Array.isArray(response.data)) {
        setCriticalElements([]);

        return;
      }

      const elements = response.data;

      const goalsPromises = elements.map((element) =>
        api
          .getAllCriticalElementGoals({
            userId: currentUser.data.oktaUserId,
            criticalElementId: element.id,
            year,
          })
          .then((res) => ({
            elementId: element.id,
            goals: res.data || [],
          }))
          .catch((err) => {
            console.error(
              `Error fetching goals for element ${element.id}:`,
              err
            );

            return { elementId: element.id, goals: [] };
          })
      );

      const goalsResults = await Promise.all(goalsPromises);

      const goalsMap = goalsResults.reduce((map, result) => {
        map[result.elementId] = result.goals;

        return map;
      }, {});

      const elementsWithGoals = elements.map((element) => ({
        ...element,
        goals: goalsMap[element.id] || [],
      }));

      setCriticalElements(elementsWithGoals);
      setError(null);
    } catch (err) {
      console.error('Error fetching critical elements:', err);
      setError(err.message || 'Failed to fetch critical elements');
      setCriticalElements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriticalElementsWithGoals();
  }, [clientId, year, currentUser?.data?.oktaUserId]);

  return {
    criticalElements,
    loading,
    error,
    fetchCriticalElements: fetchCriticalElementsWithGoals,
  };
};

export default useCriticalElements;
